import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { deckKeys } from '@/entities/deck';
import { listDecks } from '@/shared/api/decks';
import { ErrorMessage, LinkButton } from '@/shared/ui';

import styles from './DecksPage.module.css';

/**
 * Every deck of the user. A new deck starts from the + button: at the side of
 * the title on a wide screen, under the thumb above the navigation on a phone.
 */
export function DecksPage(): React.JSX.Element {
  const { t } = useTranslation();
  const decks = useQuery({ queryKey: deckKeys.all, queryFn: ({ signal }) => listDecks(signal) });

  return (
    <div className={styles.page}>
      <div className={styles.titleRow}>
        <h1>{t('decks.title')}</h1>
        <LinkButton
          to="/decks/new"
          variant="primary"
          className={styles.add}
          aria-label={t('decks.new')}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" className={styles.plus}>
            <path
              d="M12 5v14M5 12h14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <span className={styles.addLabel}>{t('decks.new')}</span>
        </LinkButton>
      </div>

      {decks.isError && <ErrorMessage error={decks.error} />}

      {decks.data?.length === 0 && <p className={styles.muted}>{t('decks.empty')}</p>}

      <p className={styles.muted}>
        <Link to="/decks/import">{t('import.link')}</Link>
      </p>

      {decks.data && decks.data.length > 0 && (
        <ul className={styles.decks}>
          {decks.data.map((deck) => (
            <li className={styles.deck} key={deck.id}>
              <div className={styles.deckText}>
                <Link to={`/decks/${deck.id}`} className={styles.deckName}>
                  {deck.title}
                </Link>
                {deck.description !== '' && (
                  <span className={styles.muted}>{deck.description}</span>
                )}
              </div>
              <span className={styles.muted}>{t('decks.cards', { count: deck.card_count })}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
